// Landing.js
import React, { useEffect, useState } from "react"
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  Divider,
  useMediaQuery,
  useTheme,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import { useDropzone } from "react-dropzone"
// @ts-ignore
import { useHistory } from "react-router-dom"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import FolderIcon from "@material-ui/icons/Folder"
import CustomButton from "../components/form-components/custom-button"
import useStore from "../utils/store"
import { useAlertStore } from "../utils/alert-store"
import consts from "../utils/consts.json"

// Add these imports for social media icons
import YouTubeIcon from "@material-ui/icons/YouTube"
import FacebookIcon from "@material-ui/icons/Facebook"
import LinkedInIcon from "@material-ui/icons/LinkedIn"
import TwitterIcon from "@material-ui/icons/Twitter"

const useStyles = makeStyles((theme) => ({
  root: {
    position: "relative",
    backgroundImage: "linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url(/background1.jpg)",
    backgroundSize: "cover",
    backgroundPosition: "center",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(4, 3),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(5, 4),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(6, 5),
    },
  },
  mainContent: {
    maxWidth: 1400,
    width: "100%",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      alignItems: "center",
      gap: theme.spacing(4),
    },
  },
  headerContent: {
    flex: "1 1 60%",
    marginBottom: theme.spacing(3),
    minWidth: "300px",
    [theme.breakpoints.up("md")]: {
      marginBottom: 0,
      minWidth: "500px",
    },
    [theme.breakpoints.up("lg")]: {
      minWidth: "600px",
    },
  },
  uploadContent: {
    flex: "1 1 40%",
    [theme.breakpoints.up("md")]: {
      marginTop: theme.spacing(3),
    },
  },
  poweredByAI: {
    color: theme.palette.secondary.main,
    fontWeight: 500,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontSize: "1.2rem",
  },
  title: {
    color: theme.palette.secondary.main,
    fontSize: "3.5rem",
    lineHeight: 1.2,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      fontSize: "2.5rem",
    },
    "& .analyzer-part": {
      fontSize: "0.7em",
    },
  },
  description: {
    color: theme.palette.primary.contrastText,
    maxWidth: 700,
    fontSize: "1.3rem",
    lineHeight: 1.5,
    marginBottom: theme.spacing(3),
  },
  uploadContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(4),
  },
  uploadBox: {
    border: `2px dashed ${theme.palette.primary.contrastText}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: "rgba(255, 255, 255, 0.1)",
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    minHeight: 160,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.15)",
      borderColor: theme.palette.secondary.main,
    },
  },
  uploadHeader: {
    color: theme.palette.primary.contrastText,
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    textAlign: "center",
  },
  uploadIcon: {
    color: theme.palette.primary.contrastText,
    fontSize: 48,
    marginBottom: theme.spacing(2),
    opacity: 0.8,
  },
  uploadText: {
    color: theme.palette.primary.contrastText,
    textAlign: "center",
    fontSize: "1rem",
    lineHeight: 1.4,
    opacity: 0.9,
    marginBottom: theme.spacing(0.5),
  },
  divider: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: theme.spacing(2, 0),
  },
  orText: {
    color: theme.palette.primary.contrastText,
    fontSize: "1rem",
    fontWeight: 600,
    margin: theme.spacing(0, 2),
    opacity: 0.8,
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: theme.spacing(4),
  },
  loadingText: {
    color: theme.palette.primary.contrastText,
    marginLeft: theme.spacing(2),
  },
  eySecurityReviewContainer: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(5, 3),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(6, 3),
    },
  },
  eySecurityReviewContent: {
    maxWidth: 1000,
    margin: "0 auto",
    textAlign: "center",
    fontSize: "1.3rem",
    lineHeight: 1.5,
    fontWeight: 300,
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.1rem",
    },
  },
  whatWeCoverContainer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(6, 3, 3, 3),
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(10, 3, 4, 3),
    },
  },
  whatWeCoverTitle: {
    textAlign: "center",
    fontWeight: 700,
    marginBottom: theme.spacing(3),
    fontSize: "2rem",
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(6),
      fontSize: "2.5rem",
    },
  },
  whatWeCoverContent: {
    maxWidth: 1200,
    margin: "0 auto",
  },
  itemContainer: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(0, 1),
    [theme.breakpoints.up("sm")]: {
      marginBottom: theme.spacing(4),
    },
    [theme.breakpoints.up("md")]: {
      marginBottom: theme.spacing(8),
      padding: 0,
    },
  },
  itemImage: {
    width: "100%",
    maxWidth: 500,
    height: "auto",
    padding: theme.spacing(1),
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      maxWidth: 300,
      padding: theme.spacing(0.5),
    },
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(2),
    },
  },
  featureTitle: {
    color: theme.palette.primary.main,
    fontSize: "1.5rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    textAlign: "center",
    [theme.breakpoints.up("sm")]: {
      fontSize: "1.75rem",
      marginBottom: theme.spacing(1.5),
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "2rem",
      marginBottom: theme.spacing(2),
      textAlign: "left",
    },
  },
  featureDescription: {
    fontSize: "1rem",
    lineHeight: 1.5,
    color: theme.palette.primary.light,
    textAlign: "center",
    [theme.breakpoints.up("sm")]: {
      fontSize: "1.05rem",
    },
    [theme.breakpoints.up("md")]: {
      fontSize: "1.1rem",
      lineHeight: 1.6,
      textAlign: "left",
    },
  },
  newsletterSection: {
    backgroundColor: theme.palette.grey[50],
    padding: theme.spacing(8, 3),
    textAlign: "center",
    [theme.breakpoints.up("md")]: {
      padding: theme.spacing(10, 3),
    },
  },
  newsletterContainer: {
    maxWidth: 800,
    margin: "0 auto",
  },
  newsletterSubtitle: {
    color: theme.palette.primary.light,
    fontSize: "1.1rem",
    maxWidth: 700,
    margin: "0 auto",
    marginTop: theme.spacing(3),
  },
  subscribeForm: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  subscribeButton: {
    height: 56,
    minWidth: 250,
    fontSize: "1rem",
    fontWeight: 600,
    padding: theme.spacing(0, 4),
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  footer: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(6, 3, 4),
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(4, 2, 3),
    },
  },
  footerContent: {
    maxWidth: 1200,
    margin: "0 auto",
  },
  footerTop: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    marginBottom: theme.spacing(6),
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
  },
  footerBottom: {
    display: "flex",
    flexDirection: "column",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
    },
  },
  footerLinks: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      justifyContent: "flex-start",
    },
    [theme.breakpoints.up("md")]: {
      marginBottom: 0,
      justifyContent: "flex-end",
    },
  },
  footerLink: {
    color: theme.palette.primary.main,
    textDecoration: "none",
    fontSize: "0.9rem",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  socialLinks: {
    display: "flex",
    gap: theme.spacing(2),
    marginTop: theme.spacing(3),
    [theme.breakpoints.up("md")]: {
      marginTop: 0,
    },
  },
  socialIcon: {
    width: 40,
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    border: `1px solid ${theme.palette.primary.light}`,
    transition: "background-color 0.3s",
    "&:hover": {
      backgroundColor: theme.palette.background.default,
    },
  },
  legalText: {
    fontSize: "0.75rem",
    color: theme.palette.primary.light,
    textAlign: "left",
    lineHeight: 1.5,
    maxWidth: "70%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      marginBottom: theme.spacing(3),
    },
  },
}))

export default function Landing() {
  const classes = useStyles()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"))

  const history = useHistory()
  const [loading, setLoading] = useState(false)

  const containsSolFiles = useStore((state) => state.containsSolFiles)
  const unzipFiles = useStore((state) => state.unzipFiles)
  const showAlert = useAlertStore((state) => state.showAlert)
  const analyzeSolidityVersions = useStore(
    (state) => state.analyzeSolidityVersions,
  )
  const setVersionInfo = useStore((state) => state.setVersionInfo)
  const getAllContracts = useStore((state) => state.getAllContracts)
  const findMainContractAndMainFile = useStore(
    (state) => state.findMainContractAndMainFile,
  )
  const setMainContract = useStore((state) => state.setMainContract)
  const setMainFile = useStore((state) => state.setMainFile)
  const setAllContracts = useStore((state) => state.setAllContracts)
  const setUploadedSolFiles = useStore((state) => state.setUploadedSolFiles)

  const whatWeCoverData = [
    {
      title: "Security AI Assistant",
      description:
        "AI agent automatically builds hundreds of test scenarios based on design requirements and commonly known vulnerabilities.",
      imgURL: "/security-AI-assistant.png",
    },
    {
      title: "Reduced Risk Exposure",
      description:
        "Automated testing and structured analysis identifies bugs, vulnerabilities and logic flaws before deployment, helping mitigate critical blockchain security and operational risks.",
      imgURL: "/reduced-risk-exposure.png",
    },
    {
      title: "Contract Testing",
      description:
        "Tests directly from the developer environment by connecting through the EY API.",
      imgURL: "/contract-testing.png",
    },
    {
      title: "Access Control Vulnerabilities",
      description:
        "Our tool analyzes smart contract permissions and access controls to identify vulnerable points, where unauthorized access could lead to fund manipulation or contract takeover.",
      imgURL: "/access-control-vulnerabilities.png",
    },
    {
      title: "Reentrancy Exploits",
      description:
        "We detect potential reentrancy vulnerabilities that could allow attackers to recursively call functions and drain funds before state updates occur, a common issue in DeFi applications.",
      imgURL: "/reentrancy-exploits.png",
    },
    {
      title: "Input Validation Weaknesses",
      description:
        "Our system checks for proper validation of external inputs, ensuring contracts reject malformed data that could lead to unexpected behavior or exploitation.",
      imgURL: "/input-validation-weaknesses.png",
    },
    {
      title: "External Call Vulnerabilities",
      description:
        "We identify unsafe external calls to untrusted contracts that could lead to security risks, ensuring proper validation and error handling for all interactions.",
      imgURL: "/external-call-vulnerabilities.png",
    },
  ]

  const MIN_LOADING_TIME = 800

  const clearMainContractAndVersionInfo = () => {
    setVersionInfo(null)
    setMainContract(null)
    setAllContracts([])
  }

  const handleDropZip = async (acceptedFiles) => {
    clearMainContractAndVersionInfo()
    if (acceptedFiles.length === 0) {
      showAlert({
        message: "Please upload a valid ZIP file.",
        type: "error",
      })
      return
    }
    setLoading(true)
    const start = Date.now()
    try {
      const file = acceptedFiles[0]
      let unzippedFiles = await unzipFiles(file)

      const hasSol = containsSolFiles(unzippedFiles)
      const elapsed = Date.now() - start
      if (elapsed < MIN_LOADING_TIME) {
        await new Promise((res) => setTimeout(res, MIN_LOADING_TIME - elapsed))
      }
      if (!hasSol) {
        showAlert({
          message: "No Solidity (.sol) files found in the ZIP archive.",
          type: "error",
        })
        setLoading(false)
        return
      }

      const filesWithContent = await Promise.all(
        unzippedFiles.map(async (f) => ({
          path: f.name,
          content:
            f.file && typeof f.file.text === "function"
              ? await f.file.text()
              : f.text,
        })),
      )
      setUploadedSolFiles(filesWithContent)

      const solFiles = unzippedFiles.filter(
        (f) => f.name && f.name.endsWith(".sol"),
      )
      const versionInfo = await analyzeSolidityVersions(solFiles)
      setVersionInfo(versionInfo)
      const allContracts = await getAllContracts(solFiles)
      setAllContracts(allContracts || [])
      const response = await findMainContractAndMainFile(solFiles, showAlert)
      if (response) {
        setMainContract(response.mainContract)
        setMainFile(response.mainFile)
      }
      setLoading(false)
      history.push(consts.ROUTES.COMPILATION)
    } catch (err) {
      showAlert({
        message: "Failed to read ZIP file. Please try again.",
        type: "error",
      })
      setLoading(false)
    }
  }

  const handleDropFolder = async (acceptedFiles) => {
    clearMainContractAndVersionInfo()
    if (acceptedFiles.length === 0) {
      showAlert({
        message: "Please upload a valid project folder.",
        type: "error",
      })
      return
    }
    setLoading(true)
    const start = Date.now()
    const hasSol = containsSolFiles(acceptedFiles)
    const elapsed = Date.now() - start
    if (elapsed < MIN_LOADING_TIME) {
      await new Promise((res) => setTimeout(res, MIN_LOADING_TIME - elapsed))
    }
    if (!hasSol) {
      showAlert({
        message: "No Solidity (.sol) files found in the folder.",
        type: "error",
      })
      setLoading(false)
      return
    }

    const filesWithContent = await Promise.all(
      acceptedFiles.map(async (f) => ({
        // react-dropzone puts the relative path in `f.path` (leading slash) or
        // `webkitRelativePath`; fall back to `f.name` for single-file drops.
        path: (f.path || f.webkitRelativePath || f.name).replace(/^\/+/, ""),
        content:
          f.file && typeof f.file.text === "function"
            ? await f.file.text()
            : typeof f.text === "function"
            ? await f.text()
            : f.text || f.content || "",
      })),
    )
    setUploadedSolFiles(filesWithContent)

    const solFiles = acceptedFiles.filter(
      (f) => f.name && f.name.endsWith(".sol"),
    )
    const versionInfo = await analyzeSolidityVersions(solFiles)
    setVersionInfo(versionInfo)
    const allContracts = await getAllContracts(solFiles)
    setAllContracts(allContracts || [])
    const response = await findMainContractAndMainFile(solFiles, showAlert)
    if (response) {
      setMainContract(response.mainContract)
      setMainFile(response.mainFile)
    }
    setLoading(false)
    history.push(consts.ROUTES.COMPILATION)
  }

  const { getRootProps: getZipRootProps, getInputProps: getZipInputProps } =
    useDropzone({
      onDrop: handleDropZip,
      accept: {
        "application/zip": [".zip"],
      },
    })

  useEffect(() => {
    const el = document.querySelectorAll("input[type=file]")?.[0]
    if (el) {
      //@ts-ignore
      el.webkitdirectory = true
    }
  }, [])

  const {
    getRootProps: getFolderRootProps,
    getInputProps: getFolderInputProps,
  } = useDropzone({
    onDrop: handleDropFolder,
  })

  const handleSubscribe = () => {
    window.open(
      "https://www.ey.com/en_gl/functional/forms/subscription/subscribe-to-the-ey-blockchain-smart-contract-security-newsletter",
      "_blank"
    )
  }

  return (
    <Box>
      {/* Hero Section */}
      <Box className={classes.root}>
        <Box className={classes.mainContent}>
          {/* Left side - Header content */}
          <Box className={classes.headerContent}>
            <Typography variant="h1" className={classes.title}>
              <span className="analyzer-part">EY Blockchain Analyzer:</span>
              <br />
              Smart Contract & Token Review
            </Typography>
            <Typography className={classes.poweredByAI}>
              Powered by AI
            </Typography>
            <Typography className={classes.description}>
              EY's AI enabled Smart Contract & Token Review solution helps
              organizations proactively detect vulnerabilities, validate logic,
              and reduce risk. Unlocking scalable secure adoption of Web3
              technologies across NFTs, stablecoins, and other
              blockchain-enabled applications.
            </Typography>
          </Box>

          {/* Right side - Upload section */}
          <Box className={classes.uploadContent}>
            <Box className={classes.uploadContainer}>
              <Box>
                <Typography variant="h6" className={classes.uploadHeader}>
                  Upload Solidity project
                </Typography>
                <Box {...getFolderRootProps()} className={classes.uploadBox}>
                  <input {...getFolderInputProps()} id={"upload-folder"} />
                  <FolderIcon className={classes.uploadIcon} />
                  <Typography className={classes.uploadText}>
                    Drag and drop a full project folder.
                  </Typography>
                  <Typography className={classes.uploadText}>
                    This option analyzes multi-file projects including imports
                    and configurations.
                  </Typography>
                </Box>
              </Box>

              {/* Divider */}
              <Box className={classes.divider}>
                <Divider style={{ flex: 1, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
                <Typography className={classes.orText}>OR</Typography>
                <Divider style={{ flex: 1, backgroundColor: 'rgba(255, 255, 255, 0.3)' }} />
              </Box>

              {/* ZIP file upload */}
              <Box>
                <Typography variant="h6" className={classes.uploadHeader}>
                  Upload ZIP file
                </Typography>
                <Box {...getZipRootProps()} className={classes.uploadBox}>
                  <input {...getZipInputProps()} id={"upload-zip"}/>
                  <CloudUploadIcon className={classes.uploadIcon} />
                  <Typography className={classes.uploadText}>
                    Drag and drop a ZIP archive containing your Solidity files.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Loading indicator */}
        {loading && (
          <Box className={classes.loadingContainer}>
            <CircularProgress style={{ color: theme.palette.primary.contrastText }} />
            <Typography className={classes.loadingText}>
              Analyzing your smart contracts...
            </Typography>
          </Box>
        )}
      </Box>

      {/* Testimonial Section */}
      <Box className={classes.eySecurityReviewContainer}>
        <Typography variant="h5" className={classes.eySecurityReviewContent}>
          Built on years of research and client collaboration, EY's solution now
          integrates advanced AI to accelerate and scale smart contract reviews.
          With a modern UI and API, clients can independently test contracts
          across EVM-based networks.
        </Typography>
      </Box>

      {/* What We Cover Section */}
      <Box className={classes.whatWeCoverContainer}>
        <Typography variant="h2" className={classes.whatWeCoverTitle}>
          Key features
        </Typography>
        <Box className={classes.whatWeCoverContent}>
          {whatWeCoverData.map((item, index) => (
            <Grid
              container
              key={index}
              className={classes.itemContainer}
              direction={
                isMobile ? "column" : index % 2 === 0 ? "row" : "row-reverse"
              }
              alignItems="center"
              spacing={isMobile ? 2 : 4}
            >
              <Grid item xs={12} md={6}>
                <Box className={classes.itemImage}>
                  <img
                    src={item.imgURL}
                    alt={item.title}
                    style={{
                      maxWidth: "100%",
                      height: isMobile ? "150px" : "200px",
                      width: isMobile ? "auto" : "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h3" className={classes.featureTitle}>
                  {item.title}
                </Typography>
                <Typography className={classes.featureDescription}>
                  {item.description}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Box>
      </Box>

      {/* Disclaimer Section */}
      <Box>
        {/* Top Separator */}
        <Box style={{ 
          height: "1px",
          background: `linear-gradient(to right, transparent, ${theme.palette.grey[300]} 35%, ${theme.palette.grey[300]} 65%, transparent)`,
        }} />
        
        <Box style={{ 
          backgroundColor: theme.palette.background.paper,
          padding: theme.spacing(4, 3),
          textAlign: "center",
        }}>
          <Box style={{ maxWidth: 1000, margin: "0 auto" }}>
            <Typography style={{ 
              fontStyle: "italic",
              fontSize: "0.95rem",
              lineHeight: 1.6,
              color: theme.palette.text.secondary
            }}>
            * Use of this tool does not constitute an audit, examination, review of any other form of assurance engagement under the American Institute of Certified Public Accountants (AICPA) Statements on Auditing Standards (SASs) or Statements on Standards for Attestation Engagements (SSAEs) or the International Auditing and Assurance Standards Board (IAASB) International Auditing Standards (ISAs) or International Standards on Assurance Engagements (ISAEs) and, as such, EY provides no assurance with respect to the use of this tool.
            </Typography>
          </Box>
        </Box>
        
        {/* Bottom Separator */}
        <Box style={{ 
          height: "1px",
          background: `linear-gradient(to right, transparent, ${theme.palette.grey[300]} 15%, ${theme.palette.grey[300]} 85%, transparent)`,
        }} />
      </Box>

      {/* Newsletter Section */}
      <Box className={classes.newsletterSection}>
        <Box className={classes.newsletterContainer}>
          <Box className={classes.subscribeForm}>
            <CustomButton
              variant="contained"
              onClick={handleSubscribe}
              className={classes.subscribeButton}
            >
              Subscribe to our newsletter
            </CustomButton>
          </Box>
          <Typography className={classes.newsletterSubtitle}>
            Get insights, updates, and curated content delivered to your inbox
            every week.
          </Typography>
        </Box>
      </Box>

      {/* Footer Section */}
      <Box className={classes.footer}>
        <Box className={classes.footerContent}>
          {/* Top row: Logo and Links */}
          <Box className={classes.footerTop}>
            <img 
              src="/logo-dark.svg" 
              alt="EY Logo"
              style={{ display: 'block' }}
            />

            <Box className={classes.footerLinks}>
              <a href="https://blockchain.ey.com/contactus" target="_blank" className={classes.footerLink}>
                Connect with us
              </a>
              <a href="https://www.ey.com/en_us/locations" target="_blank" className={classes.footerLink}>
                Our locations
              </a>
              <a href="https://login.ey.com/myey/login" target="_blank" className={classes.footerLink}>
                My EY
              </a>
              <a href="https://www.ey.com/en_gl/sitemap" target="_blank" className={classes.footerLink}>
                Site map
              </a>
              <a href="https://blockchain.ey.com/Terms_of_use.docx" target="_blank"className={classes.footerLink}>
                Terms of use
              </a>
              <a href="https://blockchain.ey.com/privacy_notice.docx" target="_blank" className={classes.footerLink}>
                Privacy
              </a>
            </Box>
          </Box>

          {/* Bottom row: Legal Text and Social Icons */}
          <Box className={classes.footerBottom}>
            <Typography className={classes.legalText}>
EY refers to the global organization, and may refer to one or more, of the member firms of Ernst & Young Global Limited, each of which is a separate legal entity. Ernst & Young Global Limited, a UK company limited by guarantee, does not provide services to clients.
            </Typography>

            <Box className={classes.socialLinks}>
              <a href="https://www.youtube.com/ernstandyoungglobal" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box className={classes.socialIcon} >
                  <YouTubeIcon />
                </Box>
              </a>
              <a href="https://www.facebook.com/EY/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box className={classes.socialIcon}>
                  <FacebookIcon />
                </Box>
              </a>
              <a href="https://www.linkedin.com/company/ernstandyoung/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box className={classes.socialIcon}>
                  <LinkedInIcon />
                </Box>
              </a>
              <a href="https://twitter.com/" target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                <Box className={classes.socialIcon}>
                  <TwitterIcon />
                </Box>
              </a>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
